import React from 'react';
import { useDispatch } from 'react-redux';
import { 
  FiFile, 
  FiFileText, 
  FiDatabase,
  FiX, 
  FiRotateCcw,
  FiCheck,
  FiAlertCircle,
  FiDownload,
  FiLoader
} from 'react-icons/fi';
import { 
  removeExport, 
  retryExport, 
  EXPORT_STATUS 
} from '../../store/slices/export.slice';
import exportService from '../../services/export/exportService';

/**
 * ExportTaskItem Component
 * Displays individual export task with status and controls
 * 
 * @param {Object} props - Component props
 * @param {Object} props.exportTask - Export task object from Redux store
 * @param {boolean} props.showControls - Show action buttons
 * @param {Function} props.onRetry - Custom retry callback
 * @param {Function} props.onRemove - Custom remove callback
 * @param {Function} props.onDownload - Custom download callback
 */
const ExportTaskItem = ({ 
  exportTask, 
  showControls = true,
  onRetry = null,
  onRemove = null,
  onDownload = null 
}) => {
  const dispatch = useDispatch();

  if (!exportTask) return null;

  const {
    export_id,
    export_name,
    export_type,
    status,
    file_url,
    file_name,
    error,
    startTime,
    endTime,
    retryCount
  } = exportTask;

  // Get file icon based on export type
  const getFileIcon = () => {
    const iconClass = "w-5 h-5 flex-shrink-0";

    switch (export_type?.toLowerCase()) {
      case 'excel':
      case 'xlsx':
        return <FiFileText className={`${iconClass} text-green-500`} />;
      case 'pdf':
        return <FiFile className={`${iconClass} text-red-500`} />;
      case 'csv':
        return <FiDatabase className={`${iconClass} text-blue-500`} />;
      case 'json':
        return <FiFileText className={`${iconClass} text-purple-500`} />;
      default:
        return <FiFile className={`${iconClass} text-gray-500`} />;
    }
  };

  // Get status color and icon
  const getStatusInfo = () => {
    switch (status) {
      case EXPORT_STATUS.PENDING:
        return {
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          text: 'Pending',
          icon: null
        };
      case EXPORT_STATUS.EXPORTING:
        return {
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          text: 'Exporting...',
          icon: <FiLoader className="w-4 h-4 animate-spin" />
        };
      case EXPORT_STATUS.COMPLETED:
        return {
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          text: 'Ready',
          icon: <FiCheck className="w-4 h-4" />
        };
      case EXPORT_STATUS.FAILED:
        return {
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          text: 'Failed',
          icon: <FiAlertCircle className="w-4 h-4" />
        };
      default:
        return {
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          text: 'Unknown',
          icon: null
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Calculate export duration
  const getExportDuration = () => {
    if (status === EXPORT_STATUS.COMPLETED && startTime && endTime) {
      const duration = Math.round((endTime - startTime) / 1000);
      return `Completed in ${duration}s`;
    }
    if (status === EXPORT_STATUS.EXPORTING && startTime) {
      const duration = Math.round((Date.now() - startTime) / 1000);
      return `${duration}s elapsed`;
    }
    return null;
  };

  // Handle action buttons
  const handleRetry = () => {
    if (onRetry) {
      onRetry(export_id);
    } else {
      dispatch(retryExport({ exportId: export_id }));
      // You might want to restart the export process here
      // exportService.retryExport(export_id, originalExportParams);
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(export_id);
    } else {
      dispatch(removeExport({ exportId: export_id }));
    }
  };

  const handleDownload = () => {
    if (!file_url) return;
    if (onDownload) {
      onDownload(export_id, file_url, file_name);
    } else {
      exportService.downloadFile(file_url, file_name);
    }
  };

  // Render action buttons
  const renderActionButtons = () => {
    if (!showControls) return null;

    const buttonClass = "p-1.5 rounded-md hover:bg-gray-200 transition-colors duration-150";

    return (
      <div className="flex items-center gap-1 ml-2">
        {status === EXPORT_STATUS.COMPLETED && file_url && (
          <button
            onClick={handleDownload}
            className={`${buttonClass} text-blue-600`}
            title="Download file"
          >
            <FiDownload className="w-4 h-4" />
          </button>
        )}

        {status === EXPORT_STATUS.FAILED && (
          <button
            onClick={handleRetry}
            className={buttonClass}
            title="Retry export"
          >
            <FiRotateCcw className="w-4 h-4 text-blue-600" />
          </button>
        )}

        {/* Always show close button for all export tasks */}
        <button
          onClick={handleRemove}
          className={buttonClass}
          title="Remove export task"
        >
          <FiX className="w-4 h-4 text-gray-600" />
        </button>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
      {/* Export info header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {getFileIcon()}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900 truncate" title={export_name}>
              {export_name}
            </h4>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span className="capitalize">{export_type}</span>
              {retryCount > 0 && (
                <span>• Retry {retryCount}</span>
              )}
              {file_name && status === EXPORT_STATUS.COMPLETED && (
                <span>• {file_name}</span>
              )}
            </div>
          </div>
        </div>

        {/* Status badge */}
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
          {statusInfo.icon}
          <span>{statusInfo.text}</span>
        </div>
      </div>

      {/* Status indicator bar */}
      <div className="mb-3">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              status === EXPORT_STATUS.COMPLETED 
                ? 'bg-green-500 w-full' 
                : status === EXPORT_STATUS.FAILED 
                  ? 'bg-red-500 w-full'
                  : status === EXPORT_STATUS.EXPORTING
                    ? 'bg-blue-500 w-3/4 animate-pulse'
                    : 'bg-gray-400 w-1/4'
            }`}
          />
        </div>
      </div>

      {/* Additional info and actions */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          {/* Export duration */}
          {getExportDuration() && (
            <div className="text-xs text-gray-500">
              {getExportDuration()}
            </div>
          )}

          {/* Error message */}
          {status === EXPORT_STATUS.FAILED && error && (
            <div className="text-xs text-red-600 truncate" title={error}>
              Error: {error}
            </div>
          )}

          {/* Export ready message */}
          {status === EXPORT_STATUS.COMPLETED && (
            <div className="text-xs text-green-600">
              Export ready for download
            </div>
          )}

          {/* Exporting message */}
          {status === EXPORT_STATUS.EXPORTING && (
            <div className="text-xs text-blue-600">
              Generating export file...
            </div>
          )}
        </div>

        {/* Action buttons */}
        {renderActionButtons()}
      </div>
    </div>
  );
};

/**
 * Compact version of ExportTaskItem for smaller spaces
 */
export const CompactExportTaskItem = ({ exportTask, showControls = true }) => {
  const dispatch = useDispatch();

  if (!exportTask) return null;

  const { export_id, export_name, status, file_url, file_name } = exportTask;
  const statusInfo = getStatusInfo(status);

  const handleRemove = () => {
    dispatch(removeExport({ exportId: export_id }));
  };

  const handleRetry = () => {
    dispatch(retryExport({ exportId: export_id }));
  };

  const handleDownload = () => {
    if (file_url) {
      exportService.downloadFile(file_url, file_name);
    }
  };

  return (
    <div className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded-md">
      <FiFile className="w-4 h-4 text-gray-500 flex-shrink-0" />
      
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {export_name}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
          <div 
            className={`h-1 rounded-full transition-all duration-300 ${
              status === EXPORT_STATUS.COMPLETED 
                ? 'bg-green-500 w-full' 
                : status === EXPORT_STATUS.FAILED 
                  ? 'bg-red-500 w-full'
                  : status === EXPORT_STATUS.EXPORTING
                    ? 'bg-blue-500 w-3/4'
                    : 'bg-gray-400 w-1/4'
            }`}
          />
        </div>
      </div>

      <div className={`text-xs px-2 py-1 rounded ${statusInfo.bgColor} ${statusInfo.color}`}>
        {statusInfo.text}
      </div>

      {showControls && (
        <div className="flex items-center gap-1">
          {status === EXPORT_STATUS.COMPLETED && file_url && (
            <button
              onClick={handleDownload}
              className="p-1 hover:bg-gray-200 rounded"
              title="Download"
            >
              <FiDownload className="w-3 h-3 text-blue-600" />
            </button>
          )}
          {status === EXPORT_STATUS.FAILED && (
            <button
              onClick={handleRetry}
              className="p-1 hover:bg-gray-200 rounded"
              title="Retry"
            >
              <FiRotateCcw className="w-3 h-3 text-blue-600" />
            </button>
          )}
          <button
            onClick={handleRemove}
            className="p-1 hover:bg-gray-200 rounded"
            title="Remove"
          >
            <FiX className="w-3 h-3 text-gray-600" />
          </button>
        </div>
      )}
    </div>
  );
};

// Helper function for status info (used by both components)
const getStatusInfo = (status) => {
  switch (status) {
    case EXPORT_STATUS.PENDING:
      return { color: 'text-gray-500', bgColor: 'bg-gray-100', text: 'Pending' };
    case EXPORT_STATUS.EXPORTING:
      return { color: 'text-blue-600', bgColor: 'bg-blue-100', text: 'Exporting' };
    case EXPORT_STATUS.COMPLETED:
      return { color: 'text-green-600', bgColor: 'bg-green-100', text: 'Ready' };
    case EXPORT_STATUS.FAILED:
      return { color: 'text-red-600', bgColor: 'bg-red-100', text: 'Failed' };
    default:
      return { color: 'text-gray-500', bgColor: 'bg-gray-100', text: 'Unknown' };
  }
};

export default ExportTaskItem;
